import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTabsModule } from "@angular/material/tabs";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import swal from "sweetalert2";

import { MatrizRiesgoService } from "../matriz-riesgo.service";
import {
  ACTIVOS_PUEDEN_SER,
  CLASIFICACION_ACTIVOS,
  DESCRIPCION_CATEGORIA_AMENAZA,
  INTRO_ESTRATEGIAS,
  NOTAS_EXCEL,
} from "../notas-excel";
import { PantallaCompleta } from "../pantalla-completa";
import { EVENTO_PROPUESTA } from "../propuesta";
import { DatosFormato, datosFormato, fechaFormato } from "../formato-fos005";
import { claseDeCategoria, claseDeEstrategia, deDimension, deEscala } from "../matriz-comun/hoja-criterios";
import { MatrizCompletaIAComponent } from "../matriz-completa-ia/matriz-completa-ia.component";
import { RskCompletoIAComponent } from "../rsk-completo-ia/rsk-completo-ia.component";
import { ControlCambiosIAComponent } from "../control-cambios-ia/control-cambios-ia.component";
import { EditarMatrizData, EditarMatrizIAComponent } from "../editar-matriz-ia/editar-matriz-ia.component";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  ApoyoLista,
  CatAmenaza,
  Matriz,
  CatClasificacionRiesgo,
  CatClasificacionSeveridad,
  CatEstrategia,
  CatNivelRiesgo,
  CatProbabilidad,
  CatSeveridad,
  CatValoracion,
  MatrizActivo,
  MatrizVersion,
  RiesgoProceso,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";

/**
 * El libro completo FOS-005 IA-001.
 *
 * En pestañas quedan las tres hojas que se capturan: Control de Cambios, Plan
 * Gestión RSK y Activos. Las tres de criterios —Estrategias, Ejemplo de
 * Amenazas y Apreciación de Riesgos— no se editan nunca: son la referencia con
 * la que se llenan las otras, así que viven en una ventana que se abre cuando
 * hace falta consultarlas y no ocupan lugar en la barra.
 *
 * Se arman con los catálogos de la base: es el mismo contenido que en el Excel
 * vivía en hojas fijas y en notas de celda, pero ahora consultable.
 */
@Component({
  selector: "app-libro-matriz-riesgos-ia",
  standalone: true,
  templateUrl: "./libro-matriz-riesgos-ia.component.html",
  styleUrls: ["./libro-matriz-riesgos-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatTooltipModule,
    MatrizCompletaIAComponent,
    RskCompletoIAComponent,
    ControlCambiosIAComponent,
  ],
})
export class LibroMatrizRiesgosIAComponent implements OnInit, OnDestroy {
  versiones: MatrizVersion[] = [];
  riesgos: RiesgoProceso[] = [];

  /**
   * Version y fecha de liberacion DEL FORMATO, no del documento.
   *
   * Son dos numeraciones distintas y conviene no confundirlas:
   *   el FORMATO FOS-005 (la plantilla que usan todas las areas)
   *       va en la V 1.17 y se libero el 03/05/2026
   *   el DOCUMENTO (esta matriz en particular)
   *       va en su propia version, la que lista el historial de cambios,
   *       hoy la 1.7 con fecha 03/08/2026
   *
   * OJO: el encabezado del Excel maestro trae mal esta fecha —su celda
   * "Fecha Liberacion del Formato" dice 03/08/2026, que es la fecha de la
   * ultima version del CONTENIDO, no la de liberacion de la plantilla. Aqui
   * va la buena, confirmada por el usuario el 21/08/2026. No copiar de
   * vuelta esa celda sin revisarla.
   *
   * Desde el 29/09/2026 se editan en Gestion de Hojas de Apoyo, junto con el
   * titulo del formato y la organizacion (formato-fos005.ts): son los mismos
   * para todas las matrices. Mientras no se capturen valen los de arriba.
   */
  get formato(): DatosFormato {
    return datosFormato(this.apoyoListas);
  }

  get formatoLiberacion(): string {
    return fechaFormato(this.formato.liberacion);
  }

  /** Con su árbol y sus cortes: alimenta el resumen de la pestaña General. */
  activos: MatrizActivo[] = [];

  valoraciones: CatValoracion[] = [];
  probabilidades: CatProbabilidad[] = [];
  catAmenazas: CatAmenaza[] = [];
  clasificaciones: CatClasificacionRiesgo[] = [];
  nivelesRiesgo: CatNivelRiesgo[] = [];

  severidades: CatSeveridad[] = [];
  clasSeveridad: CatClasificacionSeveridad[] = [];
  estrategias: string[] = [];

  /**
   * Las mismas estrategias, pero con su descripción. La hoja de apoyo las
   * listaba como puros nombres —"Evitar", "Transferir"— cuando la explicación
   * de cada una sí está guardada y es justo lo que la hoja tiene que explicar.
   */
  catEstrategias: CatEstrategia[] = [];

  cargando = true;
  exportando = false;

  /* ---------- Ventana de criterios ---------- */

  ayudas = false;
  seccionAyuda: "estrategias" | "amenazas" | "apreciacion" = "estrategias";

  /** Las cuatro secciones de la hoja de Apreciacion, como paginas. */
  seccionApre = "valoracion";
  /* Dos paginas y no cuatro: las pildoras se encimaban en dos renglones.
   * Arriba lo que la hoja trae arriba (activos, amenazas, vulnerabilidades);
   * abajo la valoracion del riesgo con su criterio. Las claves 'amenazas' y
   * 'vulnerabilidades' ya no son pagina: sus bloques se muestran junto con
   * 'valoracion'. */
  readonly seccionesApre: { clave: string; titulo: string }[] = [
    { clave: "valoracion", titulo: "Valoración de Activos y amenazas" },
    { clave: "riesgo", titulo: "Valoración del riesgo y criterio de aceptación" },
  ];

  readonly secciones = [
    { clave: "estrategias" as const, titulo: "Estrategias",
      pie: "Qué se puede hacer con un riesgo" },
    { clave: "amenazas" as const, titulo: "Ejemplo de amenazas",
      pie: "Catálogo para clasificar cada amenaza" },
    { clave: "apreciacion" as const, titulo: "Apreciación de riesgos",
      pie: "Cómo se calcula y cuándo se acepta" },
  ];

  @ViewChild("ventanaAyudas") private ventanaAyudas?: ElementRef<HTMLElement>;

  /* ---------- Ventana del historial de cambios ---------- */

  /**
   * El historial salio de la pestaña: ahi ocupaba todo el alto y dejaba la
   * identificacion del documento enterrada arriba de una lista larga. Aqui se
   * abre cuando se necesita, que es al registrar o corregir una version.
   */
  historial = false;

  @ViewChild("ventanaHistorial") private ventanaHistorial?: ElementRef<HTMLElement>;

  /* ---------- Salto desde el resumen de General ---------- */

  /** 0 General, 1 Plan Gestion RSK, 2 Activos. */
  pestana = 0;

  @ViewChild(RskCompletoIAComponent) private hojaRsk?: RskCompletoIAComponent;
  @ViewChild(MatrizCompletaIAComponent) private hojaActivos?: MatrizCompletaIAComponent;

  /**
   * Del renglon del resumen a su tarjeta. El salto va en dos tiempos: primero
   * se cambia de pestaña y hasta que Angular la pinto se busca la tarjeta, si
   * no el scroll no encuentra el elemento.
   */
  irARiesgo(r: RiesgoProceso): void {
    this.pestana = 1;
    this.cdr.detectChanges();
    setTimeout(() => this.hojaRsk?.enfocar(r.idRiesgoProceso));
  }

  irAActivo(a: MatrizActivo): void {
    // Sin pestaña de Activos no hay a donde saltar.
    if (!this.conActivos) {
      return;
    }
    this.pestana = 2;
    this.cdr.detectChanges();
    setTimeout(() => this.hojaActivos?.enfocar(a.idMatrizActivo));
  }
  private fueraHistorial?: PantallaCompleta;

  /** La ultima registrada es la vigente; el listado ya viene en orden. */
  get versionVigente(): string {
    return this.versiones.length ? this.versiones[this.versiones.length - 1].version || "" : "";
  }

  /**
   * Cuelga la ventana de <body>. Hace falta porque la barra superior y el menú
   * lateral del sistema llegan a z-index 13051, y un elemento dibujado dentro
   * del contenido no siempre puede pasarles por encima: depende de si algún
   * ancestro abrió un contexto de apilamiento. Fuera ya no hay nada que lo
   * atrape. Es el mismo rodeo que usa la pantalla completa de las hojas.
   */
  private fuera?: PantallaCompleta;

  /** Etiquetas de los niveles, para los encabezados de la tabla de valoración. */
  readonly niveles = [1, 2, 3, 4];

  /** Notas del Excel: salen al pasar el mouse por el título. */
  notas = NOTAS_EXCEL;

  /** Introduccion de la hoja de Estrategias: se lee de la base (editable en
   *  Gestion de Hojas de Apoyo) y el texto del formato queda de respaldo. */
  readonly introEstrategias = INTRO_ESTRATEGIAS;

  /** El texto de una lista de un solo renglon, con su respaldo fijo. */
  apoyoTextoDe(lista: string, respaldo: string): string {
    const filas = this.apoyoListas.filter((a) => a.lista === lista);
    return filas.length && filas[0].texto ? filas[0].texto : respaldo;
  }

  /** Desactivado en gestion = oculto aqui. Sin renglon, siempre visible. */
  apoyoActivo(lista: string): boolean {
    const filas = this.apoyoListas.filter((a) => a.lista === lista);
    return !filas.length || filas[0].estatus === "ACTIVO";
  }

  introTitulo(): string {
    const filas = this.apoyoListas.filter((a) => a.lista === "INTRO_EST_TITULO");
    return filas.length && filas[0].texto ? filas[0].texto : this.introEstrategias.titulo;
  }

  introCuerpo(): string {
    const filas = this.apoyoListas.filter((a) => a.lista === "INTRO_EST_CUERPO");
    return filas.length && filas[0].texto ? filas[0].texto : this.introEstrategias.cuerpo;
  }

  /** Un color vivo por categoria del catalogo de amenazas (hoja-criterios.ts). */
  claseCategoria(cat?: string): string {
    return claseDeCategoria(cat);
  }

  /** El color del Excel para cada estrategia (hoja-criterios.ts). */
  claseEstrategia(nombre?: string): string {
    return claseDeEstrategia(nombre);
  }

  /** Cabecera de la hoja del Excel, para la sección de Apreciación. */
  readonly activosPuedenSer = ACTIVOS_PUEDEN_SER;
  readonly clasifActivos = CLASIFICACION_ACTIVOS;
  apoyoListas: ApoyoLista[] = [];

  /** Los textos vigentes de una lista; el contenido fijo del formato como
   *  respaldo mientras la tabla no exista. */
  apoyoDe(lista: string): string[] {
    const filas = this.apoyoListas
      .filter((a) => a.lista === lista && a.estatus === "ACTIVO")
      .map((a) => a.texto || "");
    if (filas.length) {
      return filas;
    }
    return lista === "ACTIVOS_PUEDEN_SER" ? this.activosPuedenSer
      : lista === "CLASIF_HARDWARE" ? this.clasifActivos.hardware
      : this.clasifActivos.software;
  }

  /** El primer párrafo de una nota: la definición, sin la lista de niveles. */
  definicion(clave: string): string {
    return (this.notas[clave] || "").split("\n\n")[0].replaceAll("\n", " ");
  }

  /** El renglon editable que corresponde a la definicion de cada dimension. */
  listaDimension(dim: string): string {
    const m: { [d: string]: string } = {
      CONFIDENCIALIDAD: "DEF_APRE_CONF",
      INTEGRIDAD: "DEF_APRE_INTEG",
      DISPONIBILIDAD: "DEF_APRE_DISP",
    };
    return m[dim] || "DEF_APRE_DISP";
  }

  definicionDimension(dim: string): string {
    return (this.notaDimension(dim) || "").split("\n\n")[0].replaceAll("\n", " ");
  }

  /** La nota de cada dimensión de la valoración del activo. */
  notaDimension(dim: string): string {
    const clave: { [d: string]: string } = {
      CONFIDENCIALIDAD: "confidencialidad",
      INTEGRIDAD: "integridad",
      DISPONIBILIDAD: "disponibilidad",
    };
    return this.notas[clave[dim]] || "";
  }
  readonly valoresActivo = [0, 1, 2, 3, 4, 5];

  /** Los nombres que usa el Excel para los niveles 1 a 4 y sus iniciales:
   *  la tabla de valoracion encabeza con "Amenaza Baja" y "B/M/A/MA". */
  readonly etiquetasNivel = ["Baja", "Media", "Alta", "Muy Alta"];
  readonly inicialesNivel = ["B", "M", "A", "MA"];

  /* ---------- Gestión de Riesgos: qué matriz se está viendo ----------
   * La ruta 'matriz/:idMatriz' trae la matriz del área; la ruta histórica
   * 'matrizRiesgosIA' no trae parámetro y cae en la 1, que es la de IA. */

  idMatriz = 1;
  /** La cabecera: nombre, clave del folio y si trabaja con activos. */
  matriz: Matriz | null = null;

  /** Sólo TI/IA trabaja con activos; las demás áreas no ven esa pestaña. */
  get conActivos(): boolean {
    // En seguro: sin cabecera leida NO se pinta la pestaña de Activos. Antes
    // se asumia que si, y un area sin activos la veia por un instante.
    return !!this.matriz && this.matriz.aplicaActivos === "SI";
  }

  /** Por que no se pudo leer la cabecera, si es que fallo. */
  errorMatriz = "";

  /**
   * Mientras la cabecera no llega —o si falla— el respaldo tiene que ser
   * NEUTRO. Antes caia en "Matriz de Riesgos IA", y en cualquier otra area
   * eso era una mentira: el encabezado decia IA y el archivo exportado se
   * bajaba llamandose asi, con el contenido de otra area adentro.
   */
  get tituloMatriz(): string {
    return (this.matriz && this.matriz.nombre) || "Matriz de riesgos";
  }

  /** Si la matriz trabaja con activos, para los textos que dependen de eso. */
  get metodologia(): string {
    return this.conActivos
      ? "Dos niveles. Por activo: valor del activo cruzado con amenaza y vulnerabilidad, escala 0 a 11. Por proceso: probabilidad por impacto, escala 1 a 16."
      : "Por proceso: probabilidad por impacto, escala 1 a 16.";
  }

  constructor(
    private service: MatrizRiesgoService,
    private ruta: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  /* ---------- Editar la matriz ----------
   * 29/09/2026, pedido de Norman: el botón Editar salió de las tarjetas del
   * tablero (ahora se abren con un clic) y vive aquí, en el encabezado de la
   * matriz. Edita lo mismo que editaba allá: nombre, área, alcance, proceso
   * y si trabaja con activos. */

  /**
   * Quien administra las matrices: el menú del tablero. Es la misma regla
   * que aplica el servidor al guardar (MatrizBuss.exigePermiso); aquí solo
   * decide si se enseña el botón.
   */
  get puedeAdministrar(): boolean {
    return tieneAccesoMenu(this.authService.getMenuList(), "gestionRiesgos");
  }

  editarMatriz(): void {
    if (!this.matriz) {
      return;
    }
    const ref = this.dialog.open<EditarMatrizIAComponent, EditarMatrizData, Matriz | undefined>(
      EditarMatrizIAComponent,
      {
        data: { matriz: this.matriz },
        panelClass: "app-standard-dialog",
        backdropClass: "app-standard-dialog-backdrop",
        width: "680px",
        maxWidth: "96vw",
        autoFocus: false,
        // Se cierra con sus botones: un clic al descuido en el fondo se
        // llevaría lo que se estaba escribiendo.
        disableClose: true,
      }
    );
    ref.afterClosed().subscribe((guardada) => {
      if (!guardada) {
        return;
      }
      this.recargarCabecera();
      // El aviso se va solo a los dos segundos, como el del tablero.
      swal.fire({ icon: "success", text: "Matriz actualizada.", timer: 2000, showConfirmButton: false });
    });
  }

  /**
   * Vuelve a leer SOLO la cabecera tras editarla: el título, el alcance y la
   * pestaña de Activos dependen de ella. Si apagaron los activos estando en
   * esa pestaña, se regresa a General.
   */
  private recargarCabecera(): void {
    const id = this.idMatriz;
    this.service.obtenerMatriz(id).subscribe({
      next: (res) => {
        if (id !== this.idMatriz || !res || res.codigoError || !res.matriz) {
          return;
        }
        this.matriz = res.matriz;
        if (!this.conActivos && this.pestana === 2) {
          this.pestana = 0;
        }
        this.cdr.detectChanges();
      },
      error: () => undefined,
    });
  }

  /**
   * De vuelta al tablero de Gestion de Riesgos. Se llega a esta pantalla por
   * dos caminos —el tablero y el menu lateral— y en los dos conviene tener la
   * salida a la vista, sin depender de que el usuario encuentre otra vez la
   * entrada del menu.
   *
   * Se navega por el router y no con la liga del menu porque el tablero puede
   * llamarse distinto en cada instalacion; la RUTA si es fija.
   */
  volverAlTablero(): void {
    this.router.navigate(["/RIESGOS/gestionRiesgos"]);
  }

  /* ---------- Cambios por aprobar (control de cambios de la matriz) ---------- */

  /** El perfil de edición: sólo él ve el botón. Llega con los catálogos. */
  puedeEditar = false;
  /** Si el control de cambios está encendido en el servidor. */
  aprobacionActiva = false;
  /** Las propuestas PENDIENTES de ESTA matriz. */
  pendientesMatriz = 0;

  /**
   * Cuántas propuestas esperan en esta matriz. La bandeja es una sola para
   * todas las matrices; el servidor dice de cuál es cada propuesta.
   */
  private cargarPendientes(): void {
    const id = this.idMatriz;
    this.service.pendientes(1).subscribe({
      next: (res) => {
        if (id !== this.idMatriz || !res || res.codigoError) {
          return;
        }
        this.aprobacionActiva = !!res.aprobacionActiva;
        // Las de matriz desconocida (su registro se borró mientras esperaban)
        // también cuentan: la bandeja las muestra con cualquier filtro, y
        // son las que hay que descartar.
        this.pendientesMatriz = (res.pendientes || []).filter((p) => p.idMatriz === id || !p.idMatriz).length;
        this.cdr.detectChanges();
      },
      // Sin el número el botón sólo se queda como estaba: no vale un aviso.
      error: () => undefined,
    });
  }

  /** Al guardar algo que quedó como propuesta (propuesta.ts). */
  @HostListener("window:" + EVENTO_PROPUESTA)
  alProponer(): void {
    this.cargarPendientes();
  }

  abrirCambiosPorAprobar(): void {
    this.router.navigate(["/RIESGOS/cambiosPorAprobar"], {
      queryParams: { idMatriz: this.idMatriz },
    });
  }

  ngOnInit(): void {
    // Angular REUSA este componente cuando solo cambia el parametro de la
    // ruta (pasar de una matriz a otra por el menu): con snapshot se quedaba
    // con los datos de la anterior. Por eso se escucha el parametro.
    this.ruta.paramMap.subscribe((p) => {
      const param = p.get("idMatriz");
      this.idMatriz = param ? Number(param) : 1;
      this.cargarTodo();
    });
  }

  private cargarTodo(): void {
    this.matriz = null;
    this.errorMatriz = "";
    // De vuelta a General. Al cambiar de matriz la pestaña se quedaba en el
    // indice anterior, y si la nueva tiene menos pestañas —porque no maneja
    // activos— se abria en la equivocada.
    this.pestana = 0;
    this.service.obtenerMatriz(this.idMatriz).subscribe({
      next: (res) => {
        if (!res || res.codigoError || !res.matriz) {
          // Sin cabecera no se sabe si la matriz maneja activos: se dice, en
          // vez de comportarse en silencio como la de IA.
          this.errorMatriz = (res && res.mensajeError)
            || "No se pudo leer la matriz. Vuelve a intentarlo o recarga la página.";
        } else {
          this.matriz = res.matriz;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMatriz = "No se pudo leer la matriz. Vuelve a intentarlo o recarga la página.";
        this.cdr.detectChanges();
      },
    });

    this.cargarCatalogos();

    this.cargarVersiones();

    this.pendientesMatriz = 0;
    this.cargarPendientes();

    // El resumen de la pestaña General necesita el arbol y los cortes, no solo
    // la cabecera de cada activo.
    this.service.consultarCompleto({ idMatriz: this.idMatriz }).subscribe({
      next: (res) => {
        this.activos = (res && res.activos) || [];
        this.cdr.detectChanges();
      },
      error: (e) => console.error("Error al consultar los activos", e),
    });

    this.service.consultarRiesgosCompleto({ idMatriz: this.idMatriz }).subscribe({
      next: (res) => {
        this.riesgos = (res && res.riesgos) || [];
        this.severidades = res.severidades || [];
        this.clasSeveridad = res.clasificaciones || [];
        this.estrategias = res.estrategias || [];
        this.cdr.detectChanges();
      },
      error: (e) => console.error("Error al cargar riesgos de proceso", e),
    });
  }

  ngOnDestroy(): void {
    // Si se navega con la ventana abierta, el nodo vive colgado de <body> y
    // Angular ya no lo alcanza: liberar() lo quita y devuelve el scroll.
    if (this.fuera) {
      this.fuera.liberar();
    }
    if (this.fueraHistorial) {
      this.fueraHistorial.liberar();
    }
  }

  /* ---------- Ventana de criterios ---------- */

  /**
   * Los catálogos que sostienen las hojas de apoyo. Se recargan cada vez que
   * se abre la ventana: se configuran en otra pantalla (Hojas de apoyo del
   * FOS-005) y sin esto habría que recargar el navegador para ver un cambio.
   */
  private cargarCatalogos(): void {
    this.service.catalogos().subscribe({
      next: (res) => {
        this.valoraciones = res.valoraciones || [];
        this.probabilidades = res.probabilidades || [];
        this.catAmenazas = res.catAmenazas || [];
        this.clasificaciones = res.clasificaciones || [];
        this.nivelesRiesgo = res.nivelesRiesgo || [];
        this.catEstrategias = res.catEstrategias || [];
        this.apoyoListas = res.apoyoListas || [];
        this.puedeEditar = !!res.puedeEditar;
        this.cargando = false;
        this.cdr.detectChanges();
      },
      error: (e) => {
        console.error("Error al cargar catálogos", e);
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  /** Sólo las vigentes: una estrategia dada de baja no se sigue proponiendo. */
  estrategiasVigentes(): CatEstrategia[] {
    return this.catEstrategias.filter((e) => e.estatus === "ACTIVO");
  }

  abrirAyudas(seccion?: "estrategias" | "amenazas" | "apreciacion"): void {
    if (seccion) {
      this.seccionAyuda = seccion;
    }
    this.cargarCatalogos();
    this.ayudas = true;
    this.cdr.detectChanges();
    this.moverVentana(true);
  }

  cerrarAyudas(): void {
    // Se regresa el nodo a su lugar ANTES de ocultarlo: si se oculta primero,
    // Angular podría quitarlo del árbol con el nodo todavía colgado de <body>.
    this.moverVentana(false);
    this.ayudas = false;
    this.cdr.detectChanges();
  }

  private moverVentana(abrir: boolean): void {
    if (!this.ventanaAyudas) {
      return;
    }
    if (!this.fuera) {
      this.fuera = new PantallaCompleta(this.ventanaAyudas.nativeElement);
    }
    this.fuera.sincronizar(abrir);
  }

  /* ---------- Ventana del historial ---------- */

  abrirHistorial(): void {
    this.historial = true;
    this.cdr.detectChanges();
    this.moverHistorial(true);
  }

  /**
   * Se cierra solo con su boton: adentro se captura, y un clic al descuido en
   * el fondo o un Escape se llevaria una version a medio escribir.
   */
  cerrarHistorial(): void {
    this.moverHistorial(false);
    this.historial = false;
    this.cdr.detectChanges();
  }

  private moverHistorial(abrir: boolean): void {
    if (!this.ventanaHistorial) {
      return;
    }
    if (!this.fueraHistorial) {
      this.fueraHistorial = new PantallaCompleta(this.ventanaHistorial.nativeElement);
    }
    this.fueraHistorial.sincronizar(abrir);
  }

  @HostListener("document:keydown.escape")
  escape(): void {
    if (this.ayudas) {
      this.cerrarAyudas();
    }
  }

  /**
   * También la llama el historial de abajo cuando registra, edita o elimina
   * una versión: la ficha de arriba muestra la vigente y debe seguirle el paso.
   */
  cargarVersiones(): void {
    this.service.versiones(this.idMatriz).subscribe({
      next: (res) => {
        this.versiones = res.versiones || [];
        this.cdr.detectChanges();
      },
      error: (e) => console.error("Error al cargar el control de cambios", e),
    });
  }

  /* ---------- Apoyos de presentación ---------- */

  valoracionPor(dimension: string): CatValoracion[] {
    return deDimension(this.valoraciones, dimension);
  }

  probabilidadPor(aplicaA: string): CatProbabilidad[] {
    return deEscala(this.probabilidades, aplicaA);
  }

  severidadPor(aplicaA: string): CatSeveridad[] {
    return deEscala(this.severidades, aplicaA);
  }

  /** Del 4 al 1, como lista el Excel sus criterios. */
  severidadDesc(aplicaA: string): CatSeveridad[] {
    return this.severidadPor(aplicaA).slice().reverse();
  }

  /**
   * La tinta de fila que usa el Excel en el CRITERIO DE ACEPTACION del
   * riesgo del activo: Bajo casi blanco, Medio rosa, Alto azul claro y Muy
   * Alto rojo suave. Por coincidencia parcial, con MUY ALT antes que ALT.
   */
  tintaBanda(c?: string): string {
    const t = (c || "").toUpperCase();
    if (t.indexOf("MUY ALT") >= 0) { return "t-rojo"; }
    if (t.indexOf("ALT") >= 0) { return "t-azul"; }
    if (t.indexOf("MEDI") >= 0) { return "t-rosa"; }
    if (t.indexOf("BAJ") >= 0) { return "t-blanco"; }
    return "";
  }

  /** El color vivo del Excel para la banda de severidad, en la celda del
   *  criterio. Por coincidencia parcial, igual que las demas bandas. */
  claseFilaSev(c?: string): string {
    const t = (c || "").toUpperCase();
    if (t.indexOf("CRITIC") >= 0 || t.indexOf("CRÍTIC") >= 0) { return "c-rojo"; }
    if (t.indexOf("ALT") >= 0) { return "c-naranja"; }
    if (t.indexOf("MEDI") >= 0) { return "c-amarillo"; }
    if (t.indexOf("BAJ") >= 0) { return "c-verde"; }
    return "";
  }

  get categoriasAmenaza(): string[] {
    const vistas: string[] = [];
    for (const a of this.catAmenazas) {
      if (a.categoria && vistas.indexOf(a.categoria) === -1) {
        vistas.push(a.categoria);
      }
    }
    return vistas;
  }

  /** La descripcion del Excel para la categoria; vacia si es una nueva. */
  descripcionCategoria(cat: string): string {
    return DESCRIPCION_CATEGORIA_AMENAZA[(cat || "").trim().toUpperCase()] || "";
  }

  amenazasDe(categoria: string): CatAmenaza[] {
    return this.catAmenazas.filter((a) => a.categoria === categoria);
  }

  /** Celda de la tabla de valoración: valor x amenaza x vulnerabilidad. */
  celda(valor: number, amenaza: number, vulnerabilidad: number): number | null {
    const c = this.nivelesRiesgo.find(
      (n) =>
        n.valorActivo === valor &&
        n.nivelAmenaza === amenaza &&
        n.nivelVulnerabilidad === vulnerabilidad
    );
    return c && c.nivelRiesgo !== undefined ? c.nivelRiesgo : null;
  }

  /** Color según la banda, igual que en el listado de activos. */
  claseNivel(n: number | null): string {
    if (n === null) {
      return "";
    }
    for (const c of this.clasificaciones) {
      if (c.nivelDesde !== undefined && c.nivelHasta !== undefined &&
          n >= c.nivelDesde && n <= c.nivelHasta) {
        switch (c.clasificacion) {
          case "Muy Alto": return "n-muyalto";
          case "Alto": return "n-alto";
          case "Medio": return "n-medio";
          default: return "n-bajo";
        }
      }
    }
    return "";
  }

  claseSeveridad(c?: string): string {
    switch (c) {
      case "Critico": return "n-muyalto";
      case "Alta": return "n-alto";
      case "Media": return "n-medio";
      case "Baja": return "n-bajo";
      default: return "";
    }
  }

  /* ---------- Resumen de la pestaña General ---------- */

  /** El catálogo guarda la banda sin acento; en pantalla sí lleva. */
  etiquetaBanda(c?: string): string {
    return c === "Critico" ? "Crítico" : c || "";
  }

  cortesRiesgo(r: RiesgoProceso): number {
    return r.seguimientos ? r.seguimientos.length : 0;
  }

  pendientesRiesgo(r: RiesgoProceso): number {
    return (r.seguimientos || []).filter((s) => this.esPendiente(s.resultado)).length;
  }

  get pendientesRsk(): number {
    return this.riesgos.reduce((n, r) => n + this.pendientesRiesgo(r), 0);
  }

  amenazasDelActivo(a: MatrizActivo): number {
    return a.amenazas ? a.amenazas.length : 0;
  }

  /** Las acciones cuelgan de la vulnerabilidad, dos niveles abajo del activo. */
  accionesDe(a: MatrizActivo): number {
    let n = 0;
    (a.amenazas || []).forEach((am) =>
      (am.vulnerabilidades || []).forEach((v) => {
        n += (v.acciones || []).length;
      })
    );
    return n;
  }

  /** Las que todavía no se aplican: es lo que dice en qué va el plan. */
  programadasDe(a: MatrizActivo): number {
    let n = 0;
    (a.amenazas || []).forEach((am) =>
      (am.vulnerabilidades || []).forEach((v) =>
        (v.acciones || []).forEach((c) => {
          if ((c.estado || "").toLowerCase() !== "aplicada") {
            n++;
          }
        })
      )
    );
    return n;
  }

  cortesDe(a: MatrizActivo): number {
    return a.seguimientos ? a.seguimientos.length : 0;
  }

  get activosEnAlto(): number {
    return this.activos.filter(
      (a) => a.clasificacionActual === "Alto" || a.clasificacionActual === "Muy Alto"
    ).length;
  }

  get accionesProgramadas(): number {
    return this.activos.reduce((n, a) => n + this.programadasDe(a), 0);
  }

  /** Cortes cuyo resultado quedó sin capturar en el Excel original. */
  esPendiente(resultado?: string): boolean {
    return !!resultado && resultado.indexOf("NA - el resultado de este corte") === 0;
  }

  /**
   * Descarga el libro en Excel. Se usa el interceptor normal (blob) en lugar
   * de abrir la URL directo, para que viaje el token de sesión: el endpoint
   * exige sesión válida y un window.open no lleva la cabecera.
   */
  exportar(): void {
    this.exportando = true;
    this.cdr.detectChanges();
    this.service.exportarExcel(this.idMatriz).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        // El archivo lleva el nombre de SU matriz: bajar el libro de RH
        // llamado "IA-001" se prestaria a confundir un documento con otro.
        a.download = "FOS-005 " + this.tituloMatriz + ".xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
        this.exportando = false;
        this.cdr.detectChanges();
      },
      error: (e) => {
        console.error("Error al exportar a Excel", e);
        this.exportando = false;
        this.cdr.detectChanges();
      },
    });
  }
}
